import React, { useEffect, useState } from 'react';
import { CheckCircle2, Clock, Star, Shield, ArrowRight, XCircle, Check, ChevronDown, AlertCircle, TrendingDown, Zap, BookOpen, Gift, CreditCard, Utensils, ShoppingCart, Calendar, Heart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/cwGAcGzdGpTZHnhhRPjCkw/Gemini_Generated_Image_ueyx6mueyx6mueyx.png';
const LOGO_IMAGE = 'https://cdn.magicpatterns.com/uploads/23UqximhSg41Z3pH7deJQJ/ec940687-8f89-4214-af8c-549e0e28c836.png';
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 48,
    seconds: 58
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return {
          ...prev,
          seconds: prev.seconds - 1
        };
        if (prev.minutes > 0) return {
          ...prev,
          minutes: prev.minutes - 1,
          seconds: 59
        };
        if (prev.hours > 0) return {
          hours: prev.hours - 1,
          minutes: 59,
          seconds: 59
        };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  return <div className="flex items-center justify-center gap-1 sm:gap-2 text-xl sm:text-2xl font-bold text-red-600 tabular-nums" data-id="element-0">
      <div className="flex flex-col items-center bg-red-50 rounded-lg p-1.5 sm:p-2 min-w-[50px] sm:min-w-[60px]" data-id="element-1">
        <span data-id="element-2">{String(timeLeft.hours).padStart(2, '0')}</span>
        <span className="text-[10px] sm:text-xs font-normal text-red-800 uppercase" data-id="element-3">
          Horas
        </span>
      </div>
      <span className="text-red-300 pb-3 sm:pb-4" data-id="element-4">:</span>
      <div className="flex flex-col items-center bg-red-50 rounded-lg p-1.5 sm:p-2 min-w-[50px] sm:min-w-[60px]" data-id="element-5">
        <span data-id="element-6">{String(timeLeft.minutes).padStart(2, '0')}</span>
        <span className="text-[10px] sm:text-xs font-normal text-red-800 uppercase" data-id="element-7">
          Min
        </span>
      </div>
      <span className="text-red-300 pb-3 sm:pb-4" data-id="element-8">:</span>
      <div className="flex flex-col items-center bg-red-50 rounded-lg p-1.5 sm:p-2 min-w-[50px] sm:min-w-[60px]" data-id="element-9">
        <span data-id="element-10">{String(timeLeft.seconds).padStart(2, '0')}</span>
        <span className="text-[10px] sm:text-xs font-normal text-red-800 uppercase" data-id="element-11">
          Seg
        </span>
      </div>
    </div>;
};
const Accordion = ({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <motion.div className="border border-emerald-100 rounded-xl overflow-hidden bg-white mb-3 sm:mb-4 shadow-sm hover:border-emerald-300 transition-colors" initial={false} data-id="element-12">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-4 sm:p-5 text-left font-semibold text-slate-800 focus:outline-none" aria-expanded={isOpen} data-id="element-13">
        <span className="pr-4 text-sm sm:text-base" data-id="element-14">{title}</span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} data-id="element-15">
          <ChevronDown className={`w-5 h-5 flex-shrink-0 ${isOpen ? 'text-emerald-600' : 'text-emerald-500'}`} data-id="element-16" />
        </motion.div>
      </button>
      <AnimatePresence initial={false} data-id="element-17">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-18">
            <div className="p-4 sm:p-5 pt-0 text-sm sm:text-base text-slate-600 leading-relaxed border-t border-emerald-50 mt-2" data-id="element-19">
              {children}
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
};
const CTAButton = ({
  text,
  subtext,
  href = '#pricing'
}: {
  text: string;
  subtext?: string;
  href?: string;
}) => <motion.a href={href} whileHover={{
  scale: 1.02,
  y: -2
}} whileTap={{
  scale: 0.98
}} className="group relative inline-flex flex-col items-center justify-center w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-2xl shadow-[0_6px_0_0_#d97706] sm:shadow-[0_8px_0_0_#d97706] hover:shadow-[0_4px_0_0_#d97706] transition-all duration-200" data-id="element-20">
    <div className="flex items-center gap-2 text-base sm:text-xl" data-id="element-21">
      {text}
      <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" data-id="element-22" />
    </div>
    {subtext && <span className="text-xs sm:text-sm font-medium opacity-80 mt-1" data-id="element-23">
        {subtext}
      </span>}
  </motion.a>;
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.5,
  delay
}} data-id="element-24">
    {children}
  </motion.div>;
type Plan = {
  id: 'standard' | 'megapack';
  name: string;
  tagline: string;
  price: string;
  oldPrice: string;
  discount: string;
  url: string;
  badge?: string;
};
const PLANS: Plan[] = [{
  id: 'standard',
  name: 'PACK ANTI-DELIVERY',
  tagline: '6 tomos + 6 bonos',
  price: '$15.990',
  oldPrice: '$39.990',
  discount: '-60%',
  url: 'https://checkout.momentopro.shop/cart/41297520197696:1'
}, {
  id: 'megapack',
  name: 'MEGAPACK SIN EXCUSAS',
  tagline: '6 tomos + 6 bonos + 2 tomos exclusivos',
  price: '$25.990',
  oldPrice: '$64.990',
  discount: '-60%',
  url: 'https://checkout.momentopro.shop/cart/41297522262080:1',
  badge: '⭐ MÁS ELEGIDA'
}];
const OfferSelector = () => {
  const [selectedId, setSelectedId] = useState<Plan['id']>('megapack');
  const selected = PLANS.find(p => p.id === selectedId) ?? PLANS[1];
  return <div className="max-w-2xl mx-auto" data-id="element-25">
      {/* Compact selectable cards — 2 columns even on mobile so both fit without scrolling */}
      <div className="grid grid-cols-2 gap-2.5 sm:gap-4" data-id="element-26">
        {PLANS.map(plan => {
        const isSelected = plan.id === selectedId;
        const isMega = plan.id === 'megapack';
        return <motion.button key={plan.id} type="button" onClick={() => setSelectedId(plan.id)} whileTap={{
          scale: 0.98
        }} aria-pressed={isSelected} className={`relative text-left rounded-2xl p-3.5 sm:p-5 border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-amber-400 ${isSelected ? isMega ? 'bg-emerald-900 border-amber-400 shadow-xl shadow-amber-500/10' : 'bg-white border-emerald-500 shadow-lg' : isMega ? 'bg-emerald-900/40 border-emerald-700 hover:border-emerald-500' : 'bg-white/70 border-slate-200 hover:border-slate-300'}`} data-id="element-27">
              {plan.badge && <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-amber-400 text-amber-950 px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-bold whitespace-nowrap shadow" data-id="element-28">
                  {plan.badge}
                </div>}

              {/* Radio indicator */}
              <div className={`absolute top-3 right-3 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-amber-400 border-amber-400' : isMega ? 'border-emerald-600' : 'border-slate-300'}`} data-id="element-29">
                {isSelected && <Check className="w-3 h-3 text-amber-950" strokeWidth={3} data-id="element-30" />}
              </div>

              <h3 className={`text-sm sm:text-lg font-extrabold leading-tight pr-6 ${isMega ? 'text-white' : 'text-slate-900'} ${plan.badge ? 'mt-1.5 sm:mt-2' : ''}`} data-id="element-31">
                {plan.name}
              </h3>
              <p className={`text-[11px] sm:text-sm mt-0.5 mb-2 sm:mb-3 leading-snug ${isMega ? 'text-emerald-200' : 'text-slate-500'}`} data-id="element-32">
                {plan.tagline}
              </p>

              <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5" data-id="element-33">
                <span className={`line-through text-sm sm:text-lg font-semibold ${isMega ? 'text-emerald-400/70' : 'text-slate-400'}`} data-id="element-34">
                  {plan.oldPrice}
                </span>
                <span className="bg-red-500 text-white text-[9px] sm:text-xs font-extrabold px-1.5 py-0.5 rounded-md leading-none" data-id="element-35">
                  {plan.discount}
                </span>
              </div>
              <div className="flex items-baseline gap-1" data-id="element-36">
                <span className={`text-2xl sm:text-4xl font-extrabold ${isMega ? 'text-white' : 'text-slate-900'}`} data-id="element-37">
                  {plan.price}
                </span>
                <span className={`text-[11px] sm:text-sm font-medium ${isMega ? 'text-emerald-200' : 'text-slate-500'}`} data-id="element-38">
                  ARS
                </span>
              </div>
            </motion.button>;
      })}
      </div>

      {/* Shared buy button — reflects the selected plan */}
      <motion.a key={selected.id} href={selected.url} whileHover={{
      scale: 1.02,
      y: -2
    }} whileTap={{
      scale: 0.98
    }} className="group mt-4 sm:mt-6 flex flex-col items-center justify-center w-full px-6 sm:px-8 py-3.5 sm:py-4 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-2xl shadow-[0_6px_0_0_#d97706] sm:shadow-[0_8px_0_0_#d97706] hover:shadow-[0_4px_0_0_#d97706] transition-all duration-200" data-id="element-39">
        <div className="flex items-center gap-2 text-base sm:text-xl text-center" data-id="element-40">
          {selected.id === 'megapack' ? 'SÍ, QUIERO EL MEGAPACK' : 'QUIERO EL PACK ANTI-DELIVERY'}
          <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" data-id="element-41" />
        </div>
        <span className="text-xs sm:text-sm font-medium opacity-80 mt-1" data-id="element-42">
          {selected.price} ARS • Acceso inmediato
        </span>
      </motion.a>

      <div className="mt-3 sm:mt-4 flex items-center justify-center gap-3 sm:gap-4 text-[10px] sm:text-xs text-slate-300" data-id="element-43">
        <span className="flex items-center gap-1" data-id="element-44">
          <Shield className="w-3 h-3 sm:w-4 sm:h-4" data-id="element-45" /> Garantía 7 días
        </span>
        <span className="flex items-center gap-1" data-id="element-46">
          <CreditCard className="w-3 h-3 sm:w-4 sm:h-4" data-id="element-47" /> Mercado Pago
        </span>
        <span className="flex items-center gap-1" data-id="element-48">
          <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4" data-id="element-49" /> Pago seguro
        </span>
      </div>
    </div>;
};
export function App() {
  return <div className="min-h-screen bg-slate-50 font-sans text-slate-800 selection:bg-emerald-200" data-id="element-50">
      {/* Top Banner */}
      <div className="bg-emerald-900 text-emerald-50 py-1.5 sm:py-2 px-2 sm:px-4 text-center text-xs sm:text-sm font-medium flex items-center justify-center gap-1.5 sm:gap-2" data-id="element-51">
        <Star className="w-3 h-3 sm:w-4 sm:h-4 text-amber-400 fill-amber-400" data-id="element-52" />
        <span data-id="element-53">Sistema N°1 en Argentina • +500 Personas • 4.9 Rating</span>
      </div>

      {/* Hero Section */}
      <section className="pt-8 sm:pt-12 pb-12 sm:pb-20 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto overflow-hidden" data-id="element-54">
        <motion.div className="flex flex-col items-center text-center space-y-5 sm:space-y-7" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-55">
          <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs sm:text-sm font-bold tracking-wide uppercase" data-id="element-56">
            <Zap className="w-3 h-3 sm:w-4 sm:h-4" data-id="element-57" />
            Pack Anti-Delivery 6+6
          </div>

          {/* Main title (h1) */}
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight tracking-tight" data-id="element-58">
            Dejá de pensar qué cocinar cuando llegás{' '}
            <span className="text-emerald-600 relative whitespace-nowrap" data-id="element-59">
              agotado
              <svg className="absolute -bottom-1 sm:-bottom-2 left-0 w-full h-2 sm:h-3 text-amber-400" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-60">
                <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="4" fill="transparent" data-id="element-61" />
              </svg>
            </span>
          </h1>

          {/* Main mockup — displayed at 70% of its (1:1) size, centered below the title */}
          <motion.div className="relative w-[70%] max-w-md mx-auto" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-62">
            <div className="absolute inset-0 bg-emerald-200 rounded-3xl transform rotate-3 scale-105 opacity-50 blur-lg" data-id="element-63"></div>
            <img src={PRODUCT_IMAGE} alt="Pack Anti-Delivery 6+6 Tomos y Bonos" className="relative z-10 w-full h-auto rounded-2xl shadow-2xl border border-slate-200/50" data-id="element-64" />
          </motion.div>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl" data-id="element-65">
            Tu semana de comidas completamente resuelta: 21 comidas organizadas
            por tiempo, planilla semanal lista, batch cooking en 30 minutos y
            protocolo para cuando todo explota.{' '}
            <strong className="text-slate-900" data-id="element-66">
              Sin delivery de emergencia.
            </strong>
          </p>

          <div className="flex flex-wrap justify-center gap-2 sm:gap-4 text-xs sm:text-sm font-medium text-slate-600" data-id="element-67">
            <div className="flex items-center gap-1.5 sm:gap-2" data-id="element-68">
              <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500" data-id="element-69" />{' '}
              PDF Digital
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2" data-id="element-70">
              <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500" data-id="element-71" />{' '}
              Acceso Inmediato
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2" data-id="element-72">
              <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500" data-id="element-73" />{' '}
              Cualquier nivel
            </div>
          </div>

          <div className="pt-1 sm:pt-2 w-full sm:w-auto" data-id="element-74">
            <CTAButton text="💥 QUIERO MI SEMANA RESUELTA AHORA" subtext="Garantía 7 días • Pago seguro Mercado Pago" data-id="element-75" />
          </div>
        </motion.div>
      </section>

      {/* Testimonials */}
      <section className="bg-white py-8 sm:py-16 border-y border-slate-200" data-id="element-76">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-77">
          <div className="grid md:grid-cols-3 gap-3 sm:gap-8" data-id="element-78">
            {[{
            quote: 'Organicé toda mi semana de comidas en un domingo. Ahora sé exactamente qué cocinar cada noche sin pensar. Nunca más abro la heladera sin saber qué hacer.',
            author: 'Lucía M.',
            role: 'Administrativa, 2 hijos',
            location: 'Buenos Aires'
          }, {
            quote: 'Lo que más me cambió fue el batch cooking de 30 minutos. Dejé 3 días resueltos de una sola vez. Gasté menos de la mitad en delivery ese mes.',
            author: 'Rodrigo P.',
            role: 'Trabaja 10 horas por día en oficina',
            location: 'Rosario'
          }, {
            quote: 'Adapto la comida según la energía que tengo ese día en menos de 30 segundos. Antes llegaba y directamente abría Rappi sin ni intentarlo.',
            author: 'Camila F.',
            role: 'Profesional independiente, cocina para dos',
            location: 'Córdoba'
          }].map((t, i) => <motion.div key={i} whileHover={{
            y: -4
          }} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-slate-50 p-5 sm:p-6 rounded-2xl border border-slate-100 relative shadow-sm hover:shadow-md transition-shadow" data-id="element-79">
                <div className="flex gap-1 mb-3 sm:mb-4" data-id="element-80">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-3 h-3 sm:w-4 sm:h-4 fill-amber-400 text-amber-400" data-id="element-81" />)}
                </div>
                <p className="text-sm sm:text-base text-slate-700 italic mb-4 sm:mb-6" data-id="element-82">
                  "{t.quote}"
                </p>
                <div data-id="element-83">
                  <p className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-84">
                    {t.author}
                  </p>
                  <p className="text-xs sm:text-sm text-slate-500" data-id="element-85">
                    {t.location} — {t.role}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="py-8 sm:py-20 bg-slate-900 text-slate-50" data-id="element-86">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-87">
          <FadeIn data-id="element-88">
            <div className="text-center mb-6 sm:mb-16" data-id="element-89">
              <h2 className="text-2xl sm:text-4xl font-bold mb-2 sm:mb-4 text-white" data-id="element-90">
                ¿TE PASA ESTO?
              </h2>
              <p className="text-lg sm:text-xl text-slate-400" data-id="element-91">
                Si trabajás más de 8 horas por día en Argentina, esto te suena
                familiar:
              </p>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6" data-id="element-92">
            {[{
            title: 'Llegás sin saber qué cocinar',
            desc: 'Sin ningún plan para la noche, abrís la heladera y la cerrás sin agarrar nada.'
          }, {
            title: 'El delivery se come tu presupuesto',
            desc: 'Gastás más de lo que querías cada mes sin haberlo decidido conscientemente.'
          }, {
            title: 'Comprás ingredientes que se pudren',
            desc: 'Buenas intenciones el lunes, basura el viernes.'
          }, {
            title: 'Te prometés organizarte y no pasa',
            desc: 'Cada semana empezás de cero con la misma promesa que la semana anterior.'
          }, {
            title: 'La culpa aparece siempre',
            desc: "Después de cada pedido de delivery sentís que 'otra vez lo mismo'."
          }, {
            title: 'Recetas que nunca usás',
            desc: 'Tenés Instagram lleno de ideas que nunca se convierten en comida real.'
          }, {
            title: 'No sabés por dónde empezar',
            desc: 'Hay demasiadas opciones y cuando llegás cansado ninguna parece viable.'
          }, {
            title: 'Fin de mes sin control',
            desc: 'Sin un sistema, la plata del delivery se va sin que lo hayas planeado.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -10
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.05
          }} whileHover={{
            scale: 1.01,
            backgroundColor: 'rgba(30, 41, 59, 0.8)'
          }} className="flex gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-slate-800/50 border border-slate-700 cursor-default" data-id="element-93">
                <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-400 flex-shrink-0 mt-0.5 sm:mt-1" data-id="element-94" />
                <div data-id="element-95">
                  <h3 className="font-bold text-base sm:text-lg text-slate-200 mb-0.5 sm:mb-1" data-id="element-96">
                    {item.title}
                  </h3>
                  <p className="text-slate-400 text-xs sm:text-sm leading-relaxed" data-id="element-97">
                    {item.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>

          <FadeIn delay={0.2} data-id="element-98">
            <div className="mt-10 sm:mt-16 text-center" data-id="element-99">
              <p className="text-lg sm:text-2xl font-bold text-amber-400 mb-6 sm:mb-8" data-id="element-100">
                ¿Cuántas semanas más vas a improvisar qué comer?
              </p>
              <CTAButton text="QUIERO CORTAR ESTE CICLO HOY" data-id="element-101" />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-8 sm:py-20 bg-emerald-50" data-id="element-102">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-103">
          <FadeIn data-id="element-104">
            <div className="text-center mb-6 sm:mb-16" data-id="element-105">
              <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 mb-2 sm:mb-4" data-id="element-106">
                ¿PARA QUIÉN ES?
              </h2>
              <p className="text-lg sm:text-xl text-slate-600" data-id="element-107">
                Diseñado para vos si querés recuperar el control de tu
                alimentación sin complicarte la vida
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-3 sm:gap-8" data-id="element-108">
            <motion.div whileHover={{
            y: -4
          }} className="bg-white p-4 sm:p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-emerald-100 flex gap-3 sm:gap-4" data-id="element-109">
              <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600 flex-shrink-0" data-id="element-110" />
              <div data-id="element-111">
                <h3 className="font-bold text-base sm:text-xl mb-1 sm:mb-2" data-id="element-112">
                  Personas ocupadas que llegan agotadas
                </h3>
                <p className="text-xs sm:text-base text-slate-600" data-id="element-113">
                  Trabajás 8 o más horas y cuando llegás a casa no tenés energía
                  para decidir nada más.
                </p>
              </div>
            </motion.div>
            <motion.div whileHover={{
            y: -4
          }} className="bg-white p-4 sm:p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-emerald-100 flex gap-3 sm:gap-4" data-id="element-114">
              <Utensils className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600 flex-shrink-0" data-id="element-115" />
              <div data-id="element-116">
                <h3 className="font-bold text-base sm:text-xl mb-1 sm:mb-2" data-id="element-117">
                  Cocinás en tu casa o departamento
                </h3>
                <p className="text-xs sm:text-base text-slate-600" data-id="element-118">
                  Con una heladera, una hornalla y 30 minutos el domingo te
                  alcanza. Sin equipamiento especial.
                </p>
              </div>
            </motion.div>
            <motion.div whileHover={{
            y: -4
          }} className="bg-white p-4 sm:p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-emerald-100 flex gap-3 sm:gap-4 relative overflow-hidden" data-id="element-119">
              <div className="absolute top-0 right-0 bg-amber-400 text-amber-900 text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1 rounded-bl-lg" data-id="element-120">
                NUEVO
              </div>
              <Heart className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600 flex-shrink-0" data-id="element-121" />
              <div data-id="element-122">
                <h3 className="font-bold text-base sm:text-xl mb-1 sm:mb-2 pr-12" data-id="element-123">
                  Para quienes cocinan para toda la familia
                </h3>
                <p className="text-xs sm:text-base text-slate-600" data-id="element-124">
                  El sistema escala para 2, 3 o 4 personas sin duplicar el
                  trabajo ni el tiempo.
                </p>
              </div>
            </motion.div>
            <motion.div whileHover={{
            y: -4
          }} className="bg-white p-4 sm:p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-emerald-100 flex gap-3 sm:gap-4" data-id="element-125">
              <TrendingDown className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600 flex-shrink-0" data-id="element-126" />
              <div data-id="element-127">
                <h3 className="font-bold text-base sm:text-xl mb-1 sm:mb-2" data-id="element-128">
                  Querés reducir el delivery de verdad
                </h3>
                <p className="text-xs sm:text-base text-slate-600" data-id="element-129">
                  No eliminarlo — reducirlo a una decisión consciente y no a una
                  rendición por cansancio.
                </p>
              </div>
            </motion.div>
          </div>

          <FadeIn delay={0.2} data-id="element-130">
            <div className="mt-6 sm:mt-12 text-center" data-id="element-131">
              <p className="inline-flex items-center gap-1.5 sm:gap-2 text-emerald-700 text-xs sm:text-base font-medium bg-emerald-100 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full" data-id="element-132">
                <Shield className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-133" />
                ¿No estás seguro si es para vos? Tenés 7 días de garantía →
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Product Overview */}
      <section className="py-8 sm:py-20 bg-white" data-id="element-134">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-135">
          <FadeIn data-id="element-136">
            <div className="text-center mb-6 sm:mb-16" data-id="element-137">
              <div className="inline-block bg-emerald-900 text-white px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-bold tracking-wider mb-4 sm:mb-6" data-id="element-138">
                SISTEMA N°1 EN ARGENTINA
              </div>
              {/* Momento Pro logo — displayed at 400% of its base size */}
              <img src={LOGO_IMAGE} alt="Momento Pro" className="w-56 sm:w-72 h-auto mx-auto mb-4 sm:mb-6" data-id="element-139" />
              <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 mb-2 sm:mb-4" data-id="element-140">
                Para personas ocupadas que quieren comer bien sin pensar en eso
                todos los días
              </h2>
              <p className="text-lg sm:text-xl text-slate-600" data-id="element-141">
                Sistema completo · 6 Tomos + 6 Bonos · Cualquier nivel de
                organización · Todo resuelto.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center" data-id="element-142">
            <div className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-143">
              {['+120 págs.', '21 comidas', 'Planilla semanal', 'Batch cooking', 'Lista de compras', 'Árbol de decisión', 'Protocolo anti-recaída', 'Alacena inteligente', 'Calculadora de ahorro', 'Checklist imprimible', 'Semana resuelta', 'Sin delivery de emergencia'].map((feature, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.05
            }} className="flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base text-slate-700 font-medium" data-id="element-144">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500 flex-shrink-0" data-id="element-145" />
                  <span data-id="element-146">{feature}</span>
                </motion.div>)}
            </div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} className="bg-slate-50 p-5 sm:p-8 rounded-3xl border border-slate-200 mt-6 sm:mt-0" data-id="element-147">
              <h3 className="text-lg sm:text-2xl font-bold mb-4 sm:mb-6 text-slate-900" data-id="element-148">
                Resultados que podés medir:
              </h3>
              <ul className="space-y-2 sm:space-y-4" data-id="element-149">
                {['Semana resuelta en 10 min', 'Delivery reducido en 30 días', '3 días resueltos en 30 min', 'Ahorro real desde el mes 1', 'Culpa eliminada desde el día 1', 'Decisión en 30 segundos'].map((result, i) => <li key={i} className="flex items-center gap-2 sm:gap-3 text-sm sm:text-lg text-slate-700" data-id="element-150">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-151">
                      <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600" data-id="element-152" />
                    </div>
                    {result}
                  </li>)}
              </ul>
              <p className="mt-4 sm:mt-8 text-[10px] sm:text-sm text-slate-500 italic text-center" data-id="element-153">
                Funciona desde el primer domingo que lo aplicás.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="py-8 sm:py-20 bg-slate-50 border-t border-slate-200" data-id="element-154">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-155">
          <FadeIn data-id="element-156">
            <div className="text-center mb-6 sm:mb-16" data-id="element-157">
              <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 mb-2 sm:mb-4" data-id="element-158">
                CONTENIDO COMPLETO: LOS 6 TOMOS
              </h2>
              <p className="text-lg sm:text-xl text-slate-600" data-id="element-159">
                Tu hoja de ruta para eliminar el delivery de emergencia para
                siempre
              </p>
            </div>
          </FadeIn>

          <div className="space-y-0" data-id="element-160">
            <Accordion title="T1: Por qué llegás a casa sin energía para decidir (El problema que nadie nombra)" data-id="element-161">
              <p className="mb-3 sm:mb-4" data-id="element-162">
                Entendé qué es la fatiga de decisión y por qué te lleva al
                delivery todas las noches. No es falta de ganas — es biología. Y
                tiene solución concreta.
              </p>
              <ul className="list-disc pl-5 space-y-1 sm:space-y-2 text-xs sm:text-sm" data-id="element-163">
                <li data-id="element-164">Los 3 perfiles de persona ocupada</li>
                <li data-id="element-165">El ciclo silencioso lunes a domingo</li>
                <li data-id="element-166">La diferencia entre motivación y sistema</li>
              </ul>
            </Accordion>
            <Accordion title="T2: Tu diagnóstico: encontrá exactamente dónde perdés el control" data-id="element-167">
              <p data-id="element-168">
                Identificá tu punto de partida real para aplicar el sistema de
                la forma que mejor se adapte a tu rutina actual, sin forzar
                hábitos imposibles.
              </p>
            </Accordion>
            <Accordion title="T3: Las 21 comidas que no necesitan que pienses" data-id="element-169">
              <p data-id="element-170">
                Un catálogo estratégico organizado por tiempo y energía
                disponible. Desde comidas de 5 minutos hasta preparaciones de
                fin de semana.
              </p>
            </Accordion>
            <Accordion title="T4: Tu semana resuelta en 10 minutos el domingo" data-id="element-171">
              <p data-id="element-172">
                La planilla exacta que hace que la planificación sea automática.
                Llenás los casilleros, generás la lista y te olvidás hasta el
                próximo domingo.
              </p>
            </Accordion>
            <Accordion title="T5: El método del domingo: 30 minutos que liberan tu semana" data-id="element-173">
              <p data-id="element-174">
                Batch cooking realista. Sin pasar horas cocinando ni ensuciar
                toda la cocina. Dejás las bases listas para ensamblar en minutos
                durante la semana.
              </p>
            </Accordion>
            <Accordion title="T6: El sistema en piloto automático: cómo sostenerlo sin esfuerzo" data-id="element-175">
              <p data-id="element-176">
                Estrategias de retomada sin culpa cuando una semana falla.
                Porque la vida pasa, y un buen sistema contempla el caos.
              </p>
            </Accordion>
          </div>
          <p className="text-center text-xs sm:text-sm text-slate-500 mt-4 sm:mt-6" data-id="element-177">
            Tocá cada tomo para ver lo que incluye 👆
          </p>
        </div>
      </section>

      {/* Bonuses */}
      <section className="py-8 sm:py-20 bg-emerald-900 text-white" data-id="element-178">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-179">
          <FadeIn data-id="element-180">
            <div className="text-center mb-6 sm:mb-16" data-id="element-181">
              <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-amber-500 text-amber-950 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-bold tracking-wider mb-4 sm:mb-6" data-id="element-182">
                <Gift className="w-3 h-3 sm:w-4 sm:h-4" data-id="element-183" /> INCLUIDOS GRATIS
              </div>
              <h2 className="text-2xl sm:text-4xl font-bold mb-2 sm:mb-4" data-id="element-184">
                6 Bonos Estratégicos
              </h2>
              <p className="text-lg sm:text-xl text-emerald-200" data-id="element-185">
                Sin costo extra — incluidos en todas las ofertas
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-186">
            {[{
            title: 'La Alacena Inteligente',
            desc: 'Los 20 ingredientes que siempre tenés que tener para ejecutar el sistema sin ir al súper cada 2 días. Con versiones económicas para cada uno.',
            icon: ShoppingCart
          }, {
            title: 'Rescate de Lunes',
            desc: 'Qué hacer cuando llegás sin haber preparado nada. Las 5 comidas de emergencia en menos de 10 minutos para no abrir Rappi.',
            icon: AlertCircle
          }, {
            title: 'Calculadora del Gasto Real',
            desc: 'Planilla que te muestra exactamente cuánto gastás por mes en pedidos y cuánto ahorrás aplicando el sistema. Un número que cambia todo.',
            icon: TrendingDown
          }, {
            title: 'Protocolo Anti-Recaída',
            desc: 'El plan de 3 pasos para retomar el sistema después de una semana caótica, sin culpa, sin drama y sin empezar de cero.',
            icon: Shield
          }, {
            title: 'Versión Familiar',
            desc: 'Cómo escalar el sistema para cocinar para tu pareja, tus hijos o toda tu familia sin complicarte el doble.',
            icon: Heart
          }, {
            title: 'Checklist de Heladera',
            desc: 'Una hoja para imprimir y pegar en la heladera. Tildás, elegís, ejecutás. Sin apps, sin pantallas, sin pensar.',
            icon: CheckCircle2
          }].map((bono, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} whileHover={{
            y: -4,
            backgroundColor: 'rgba(6, 95, 70, 0.8)'
          }} className="bg-emerald-800/50 border border-emerald-700 p-5 sm:p-6 rounded-2xl relative overflow-hidden group transition-colors cursor-default" data-id="element-187">
                <motion.div animate={{
              scale: [1, 1.05, 1]
            }} transition={{
              repeat: Infinity,
              duration: 2,
              ease: 'easeInOut'
            }} className="absolute top-3 sm:top-4 right-3 sm:right-4 bg-amber-400 text-amber-900 text-[10px] sm:text-xs font-bold px-2 py-0.5 sm:py-1 rounded shadow-sm" data-id="element-188">
                  GRATIS
                </motion.div>
                <div className="bg-emerald-700/50 w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-emerald-600/50 transition-colors" data-id="element-189">
                  <bono.icon className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-300" data-id="element-190" />
                </div>
                <h3 className="font-bold text-base sm:text-lg mb-1.5 sm:mb-2" data-id="element-191">
                  B{i + 1}: {bono.title}
                </h3>
                <p className="text-emerald-100/80 text-xs sm:text-sm leading-relaxed" data-id="element-192">
                  {bono.desc}
                </p>
              </motion.div>)}
          </div>

          <FadeIn delay={0.2} data-id="element-193">
            <div className="mt-6 sm:mt-12 text-center" data-id="element-194">
              <p className="text-lg sm:text-2xl font-bold text-amber-400" data-id="element-195">
                VALOR TOTAL: $47.880 — GRATIS HOY
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-8 sm:py-20 bg-white" data-id="element-196">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-197">
          <FadeIn data-id="element-198">
            <div className="text-center mb-6 sm:mb-16" data-id="element-199">
              <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 mb-2 sm:mb-4" data-id="element-200">
                LA REALIDAD
              </h2>
              <p className="text-lg sm:text-xl text-slate-600" data-id="element-201">
                ¿Cuánto cuesta NO tenerlo?
              </p>
            </div>
          </FadeIn>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-slate-50 rounded-2xl sm:rounded-3xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-202">
            <div className="grid grid-cols-2 bg-slate-100 border-b border-slate-200" data-id="element-203">
              <div className="p-3 sm:p-6 text-center font-bold text-slate-500 text-sm sm:text-lg" data-id="element-204">
                Sin el sistema
              </div>
              <div className="p-3 sm:p-6 text-center font-bold text-emerald-700 text-sm sm:text-lg bg-emerald-50" data-id="element-205">
                Con el sistema
              </div>
            </div>

            {[['3-5 pedidos de delivery por semana', 'Máximo 1-2 pedidos conscientes por semana'], ['Heladera llena de ingredientes que se pudren', 'Lista de compras exacta generada automáticamente'], ['20-40 minutos decidiendo qué comer cada noche', '30 segundos usando el árbol de decisión'], ['Culpa después de cada pedido de emergencia', 'Sistema de retomada sin culpa cuando algo falla'], ['$100.000 o más por mes en delivery', 'Ahorro real medible desde el primer mes'], ['Empezás de cero cada semana', 'Planilla dominical de 10 minutos que lo resuelve todo'], ['Ingredientes comprados con buenas intenciones', 'Batch cooking de 30 minutos que deja 3 días listos'], ['Sin saber cuánto gastás realmente', 'Calculadora de ahorro con tu número exacto']].map((row, i) => <div key={i} className="grid grid-cols-2 border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors" data-id="element-206">
                <div className="p-3 sm:p-6 text-xs sm:text-base text-slate-600 flex items-start gap-2 sm:gap-3" data-id="element-207">
                  <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-208" />
                  <span data-id="element-209">{row[0]}</span>
                </div>
                <div className="p-3 sm:p-6 text-xs sm:text-base text-slate-800 font-medium bg-emerald-50/50 flex items-start gap-2 sm:gap-3" data-id="element-210">
                  <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-211" />
                  <span data-id="element-212">{row[1]}</span>
                </div>
              </div>)}
          </motion.div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-8 sm:py-20 bg-slate-900 relative overflow-hidden" data-id="element-213">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-64 bg-emerald-600/20 blur-[100px] rounded-full pointer-events-none" data-id="element-214"></div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-215">
          <FadeIn data-id="element-216">
            <div className="text-center mb-6 sm:mb-12" data-id="element-217">
              <div className="inline-block bg-red-500 text-white px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-sm font-bold tracking-wider mb-3 sm:mb-6 animate-pulse" data-id="element-218">
                OFERTA EN VIVO 60% DESCUENTO
              </div>
              <h2 className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-6" data-id="element-219">
                Elegí tu Pack Anti-Delivery
              </h2>
              <p className="text-base sm:text-xl text-slate-300 mb-6 sm:mb-8" data-id="element-220">
                Ambas opciones incluyen los 6 bonos estratégicos completos
              </p>

              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 sm:p-6 inline-block backdrop-blur-sm" data-id="element-221">
                <p className="text-slate-400 text-xs sm:text-sm font-bold uppercase tracking-wider mb-2 sm:mb-3" data-id="element-222">
                  EL LANZAMIENTO TERMINA EN
                </p>
                <CountdownTimer data-id="element-223" />
                <div className="mt-3 sm:mt-4 flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-amber-400 font-medium" data-id="element-224">
                  <Zap className="w-3 h-3 sm:w-4 sm:h-4" data-id="element-225" /> Cupos restantes 68%
                  TOMADOS
                </div>
              </div>
            </div>
          </FadeIn>

          <OfferSelector data-id="element-226" />
        </div>
      </section>

      {/* Megapack Details */}
      <section className="py-8 sm:py-20 bg-slate-50" data-id="element-227">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-228">
          <FadeIn data-id="element-229">
            <div className="text-center mb-6 sm:mb-16" data-id="element-230">
              <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 mb-2 sm:mb-4" data-id="element-231">
                ¿QUÉ AGREGA EL MEGAPACK SIN EXCUSAS?
              </h2>
              <p className="text-lg sm:text-xl text-slate-600" data-id="element-232">
                Para personas que quieren que el sistema funcione incluso cuando
                todo explota
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-4 sm:gap-8 mb-8 sm:mb-12" data-id="element-233">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-white p-6 sm:p-8 rounded-2xl border border-red-100 shadow-sm" data-id="element-234">
              <div className="text-red-500 text-sm sm:text-base font-bold mb-3 sm:mb-4 flex items-center gap-1.5 sm:gap-2" data-id="element-235">
                <XCircle className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-236" /> Sin Megapack
              </div>
              <h3 className="text-lg sm:text-xl font-bold mb-1.5 sm:mb-2" data-id="element-237">
                Abandonás en la semana 3 como siempre
              </h3>
              <p className="text-sm sm:text-base text-slate-600" data-id="element-238">
                Arrancás con energía, llegás a la semana 3 y todo vuelve a ser
                como antes. Delivery, culpa, promesa del lunes.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-emerald-900 text-white p-6 sm:p-8 rounded-2xl shadow-lg relative overflow-hidden" data-id="element-239">
              <div className="absolute -right-4 -bottom-4 opacity-10" data-id="element-240">
                <Zap className="w-24 h-24 sm:w-32 sm:h-32" data-id="element-241" />
              </div>
              <div className="text-amber-400 text-sm sm:text-base font-bold mb-3 sm:mb-4 flex items-center gap-1.5 sm:gap-2 relative z-10" data-id="element-242">
                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-243" /> Con Megapack
              </div>
              <h3 className="text-lg sm:text-xl font-bold mb-1.5 sm:mb-2 relative z-10" data-id="element-244">
                Rompé el ciclo de abandono
              </h3>
              <p className="text-sm sm:text-base text-emerald-100 relative z-10" data-id="element-245">
                Protocolo científico de instalación de hábitos en 66 días con
                seguimiento diario, puntos de control semanales y sistema de
                recompensas que hace que el hábito se sostenga solo.
              </p>
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-white p-6 sm:p-8 rounded-2xl sm:rounded-3xl border border-slate-200" data-id="element-246">
            <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-center" data-id="element-247">
              MEGAPACK SIN EXCUSAS INCLUYE:
            </h3>
            <div className="space-y-4 sm:space-y-6" data-id="element-248">
              <div className="flex gap-3 sm:gap-4" data-id="element-249">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 text-amber-700 font-bold text-lg sm:text-xl" data-id="element-250">
                  T7
                </div>
                <div data-id="element-251">
                  <h4 className="text-base sm:text-lg font-bold mb-1 sm:mb-2" data-id="element-252">
                    Cómo convertir el sistema en un hábito que se instala solo
                  </h4>
                  <p className="text-sm sm:text-base text-slate-600" data-id="element-253">
                    El protocolo de 66 días con seguimiento diario, los 5 puntos
                    de control que evitan el abandono y el sistema de
                    recompensas que hace que funcione sin esfuerzo consciente.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 sm:gap-4" data-id="element-254">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 text-amber-700 font-bold text-lg sm:text-xl" data-id="element-255">
                  T8
                </div>
                <div data-id="element-256">
                  <h4 className="text-base sm:text-lg font-bold mb-1 sm:mb-2" data-id="element-257">
                    El sistema cuando todo se rompe: viajes, salidas y semanas
                    caóticas
                  </h4>
                  <p className="text-sm sm:text-base text-slate-600" data-id="element-258">
                    Protocolo específico para viajes de trabajo, semanas con
                    compromisos seguidos, enfermedades y alta presión laboral.
                    Nunca más vas a decir "esta semana no pude".
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="py-8 sm:py-16 bg-emerald-50 border-y border-emerald-100" data-id="element-259">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-260">
          <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          type: 'spring',
          bounce: 0.4
        }} data-id="element-261">
            <Shield className="w-12 h-12 sm:w-16 sm:h-16 text-emerald-600 mx-auto mb-4 sm:mb-6" data-id="element-262" />
          </motion.div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2 sm:mb-4" data-id="element-263">
            7 DÍAS GARANTÍA TOTAL
          </h2>
          <p className="text-lg sm:text-xl text-slate-700 mb-4 sm:mb-6" data-id="element-264">
            7 días para probarlo o te devolvemos el 100%
          </p>
          <p className="text-sm sm:text-base text-slate-600 leading-relaxed" data-id="element-265">
            Abrís los tomos, probás los bonos con tu heladera y tu planilla
            real. Si no es lo que esperabas: sin preguntas, sin formularios, sin
            drama. <br data-id="element-266" />
            <span className="text-xs sm:text-sm font-medium mt-2 inline-block bg-emerald-100 px-2 sm:px-3 py-1 rounded-full" data-id="element-267">
              (Tasa de devoluciones menor al 1%)
            </span>
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-8 sm:py-20 bg-white" data-id="element-268">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-269">
          <FadeIn data-id="element-270">
            <div className="text-center mb-6 sm:mb-16" data-id="element-271">
              <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 mb-2 sm:mb-4" data-id="element-272">
                DUDAS COMUNES
              </h2>
              <p className="text-lg sm:text-xl text-slate-600" data-id="element-273">
                Preguntas Frecuentes
              </p>
            </div>
          </FadeIn>

          <div className="space-y-0" data-id="element-274">
            {['¿Cómo recibo el pack después de comprar?', '¿Sirve si nunca me organicé con la comida o es para personas que ya cocinan?', '¿Necesito saber cocinar bien para aplicar el sistema?', '¿Los 6 bonos están incluidos en las dos ofertas?', '¿Sirve si cocino para toda mi familia y no solo para mí?', '¿Sirve si tengo presupuesto ajustado para las compras del mes?', '¿Qué diferencia tiene con recetas de Instagram o YouTube?', '¿Por qué elegir el Megapack y no el Pack Anti-Delivery estándar?', '¿Tiene garantía real?'].map((q, i) => <Accordion key={i} title={q} data-id="element-275">
                <p data-id="element-276">
                  La respuesta detallada a esta pregunta se encuentra en el
                  interior del material o contactando a nuestro soporte. El
                  sistema está diseñado para cubrir todas estas inquietudes
                  desde el día 1.
                </p>
              </Accordion>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-8 sm:py-20 bg-slate-900 text-center" data-id="element-277">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-278">
          <FadeIn data-id="element-279">
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-4 sm:mb-6" data-id="element-280">
              No dejes pasar esta oportunidad
            </h2>
            <p className="text-lg sm:text-xl text-slate-300 mb-6 sm:mb-8" data-id="element-281">
              Termina en
            </p>
            <div className="flex justify-center mb-6 sm:mb-8" data-id="element-282">
              <CountdownTimer data-id="element-283" />
            </div>
            <OfferSelector data-id="element-284" />
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-6 sm:py-12 border-t border-slate-800 text-center text-slate-400" data-id="element-285">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-286">
          <div className="mb-6 sm:mb-8" data-id="element-287">
            <h3 className="text-xl sm:text-2xl font-bold text-white flex items-center justify-center gap-2 mb-3 sm:mb-4" data-id="element-288">
              <Utensils className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500" data-id="element-289" />
              PACK ANTI-DELIVERY™
            </h3>
            <p className="max-w-md mx-auto text-xs sm:text-sm" data-id="element-290">
              El sistema de organización de comidas N°1 para personas ocupadas
              en Argentina. Porque comer bien no debería depender de cuánta
              energía te queda cuando llegás a casa.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-8 text-xs sm:text-sm mb-6 sm:mb-8" data-id="element-291">
            <a href="mailto:soportemomentopro@gmail.com" className="hover:text-emerald-400 transition-colors" data-id="element-292">
              soportemomentopro@gmail.com
            </a>
            <span className="hidden sm:inline" data-id="element-293">•</span>
            <a href="#" className="hover:text-emerald-400 transition-colors" data-id="element-294">
              @momentopro.shop
            </a>
            <span className="hidden sm:inline" data-id="element-295">•</span>
            <a href="#" className="hover:text-emerald-400 transition-colors" data-id="element-296">
              momentopro.shop
            </a>
          </div>

          <p className="text-[10px] sm:text-xs text-slate-600" data-id="element-297">
            © MomentoPro.shop™ 2025 — Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>;
}